/**
 * GroupIntelligencePanel — cohort-level analytics for partner orgs.
 *
 * Shows the discriminating-trait list, cohort weakness gauges, and
 * Claude-generated recruitment + program recommendations for a single
 * group_name (e.g. "MTW"). Wires to GET /groups/{name}/intelligence.
 */
import React, { useEffect, useState } from 'react';
import { Download, Loader2, Users, TrendingDown, TrendingUp, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { appendAuthToken } from '../services/auth';
import dataService from '../services/data';

type Trait = {
  trait: string;
  strong_freq: number;
  weak_freq: number;
  lift: number;
  support_strong: number;
  support_weak: number;
  p_value: number;
};

type Weakness = {
  dimension: string;
  cohort_median: number;
  pct_below_50: number;
  pct_below_30: number;
  narrative?: string;
};

type Recommendation = {
  title: string;
  rationale: string;
  expected_lift_pp?: number;
  evidence?: string[];
  addresses?: string;
};

type Report = {
  group: string;
  size: number;
  computed_at: string;
  composite_histogram?: Array<{ bucket_start: number; count: number }>;
  strong_company_profile: {
    n_strong: number;
    n_weak: number;
    composite_formula: string;
    discriminating_traits: Trait[];
    narrative: string;
    note?: string;
  } | null;
  common_weaknesses: {
    crs_dimensions: Weakness[];
    rubric_criteria: Weakness[];
    narrative: string;
  } | null;
  recommended_actions: {
    recruitment: Recommendation[];
    programs: Recommendation[];
    narrative: string;
  } | null;
  note?: string;
};

const GroupIntelligencePanel: React.FC = () => {
  const [groups, setGroups] = useState<Array<{ name: string; count: number }>>([]);
  const [selected, setSelected] = useState<string>('');
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dataService.fetchCompanyGroups().then((res) => {
      if (res.data && res.data.length) {
        setGroups(res.data);
        // Prefer "MTW" if present, else first group
        const mtw = res.data.find((g) => g.name.toLowerCase() === 'mtw');
        setSelected(mtw ? mtw.name : res.data[0].name);
      }
    });
  }, []);

  const loadReport = React.useCallback(
    async (group: string, refresh = false) => {
      if (!group) return;
      setLoading(true);
      setError(null);
      try {
        const resp = await api.get(`/groups/${encodeURIComponent(group)}/intelligence`, {
          params: refresh ? { refresh: true } : {},
          timeout: 30000,
        });
        setReport(resp.data);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load group intelligence');
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (selected) loadReport(selected);
  }, [selected, loadReport]);

  const downloadPdf = () => {
    if (!selected) return;
    const apiBase = process.env.REACT_APP_API_URL || '/api/v1';
    window.open(
      appendAuthToken(`${apiBase}/groups/${encodeURIComponent(selected)}/intelligence/pdf`),
      '_blank'
    );
  };

  if (!groups.length) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Users className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Group Intelligence</h2>
            <p className="text-xs text-gray-500">
              Cohort-level analytics: who's strong, where you're weak, what to do.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
          >
            {groups.map((g) => (
              <option key={g.name} value={g.name}>
                {g.name} ({g.count})
              </option>
            ))}
          </select>
          <button
            onClick={() => loadReport(selected, true)}
            className="px-2 py-1.5 text-gray-600 hover:text-gray-900"
            title="Refresh (bypass cache)"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
            disabled={loading || !report}
          >
            <Download className="w-4 h-4" />
            PDF
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Analyzing cohort…
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {report && !loading && (
        <div className="space-y-6">
          {/* Header summary */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-gray-900">{report.size}</div>
              <div className="text-xs text-gray-600">Cohort size</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-700">
                {report.strong_company_profile?.n_strong ?? '—'}
              </div>
              <div className="text-xs text-gray-600">Top quartile</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-amber-700">
                {report.strong_company_profile?.n_weak ?? '—'}
              </div>
              <div className="text-xs text-gray-600">Bottom quartile</div>
            </div>
          </div>

          {/* Composite histogram */}
          {report.composite_histogram && report.composite_histogram.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Composite-score distribution</h3>
              <div className="flex items-end gap-1 h-20">
                {report.composite_histogram.map((b) => {
                  const max = Math.max(
                    ...report.composite_histogram!.map((x) => x.count),
                  );
                  const h = max > 0 ? (b.count / max) * 100 : 0;
                  return (
                    <div
                      key={b.bucket_start}
                      className="flex-1 bg-indigo-200 hover:bg-indigo-400 rounded-sm relative group"
                      style={{ height: `${h}%` }}
                      title={`${b.bucket_start}-${b.bucket_start + 9}: ${b.count}`}
                    >
                      <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-gray-500 hidden group-hover:block">
                        {b.count}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                {report.composite_histogram.map((b) => (
                  <div key={b.bucket_start} className="flex-1 text-center">
                    {b.bucket_start}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strong-company traits */}
          {report.strong_company_profile && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                What defines your strongest companies
              </h3>
              {report.strong_company_profile.note && (
                <div className="text-xs text-amber-700 bg-amber-50 rounded p-2 mb-2">
                  Note: {report.strong_company_profile.note}
                </div>
              )}
              <p className="text-sm text-gray-700 italic mb-3">
                {report.strong_company_profile.narrative}
              </p>
              <div className="space-y-1">
                {report.strong_company_profile.discriminating_traits
                  .slice(0, 5)
                  .map((t) => {
                    // Bar width: lift up to 4x → 100%
                    const w = Math.min(100, (Math.abs(t.lift - 1) / 3) * 100);
                    const pos = t.lift >= 1.5;
                    return (
                      <div key={t.trait} className="flex items-center gap-2 text-sm">
                        <div className="w-48 text-gray-700 truncate" title={t.trait}>
                          {t.trait}
                        </div>
                        <div className="flex-1 bg-gray-100 rounded h-3 relative">
                          <div
                            className={`h-3 rounded ${pos ? 'bg-green-500' : 'bg-red-400'}`}
                            style={{ width: `${w}%` }}
                          />
                        </div>
                        <div className="w-24 text-xs text-gray-600 text-right">
                          {t.lift}× • n={t.support_strong}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Weakness gauges */}
          {report.common_weaknesses && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <TrendingDown className="w-4 h-4 text-amber-600" />
                Where the cohort is broadly weak
              </h3>
              <p className="text-sm text-gray-700 italic mb-3">
                {report.common_weaknesses.narrative}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  ...report.common_weaknesses.crs_dimensions,
                  ...report.common_weaknesses.rubric_criteria,
                ]
                  .slice(0, 3)
                  .map((w) => (
                    <div
                      key={w.dimension}
                      className="bg-amber-50 border border-amber-200 rounded-lg p-3"
                    >
                      <div className="text-xs uppercase tracking-wide text-amber-700 font-medium">
                        {w.dimension.replace(/_/g, ' ')}
                      </div>
                      <div className="flex items-baseline gap-2 mt-1">
                        <div className="text-3xl font-bold text-amber-900">
                          {w.cohort_median}
                        </div>
                        <div className="text-xs text-gray-600">median</div>
                      </div>
                      <div className="text-xs text-gray-700 mt-1">
                        {Math.round(w.pct_below_50 * 100)}% below 50 ·{' '}
                        {Math.round(w.pct_below_30 * 100)}% below 30
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Recommended actions */}
          {report.recommended_actions && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Recommended actions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold text-green-700 uppercase mb-1">
                    Recruitment
                  </div>
                  <ul className="space-y-2">
                    {report.recommended_actions.recruitment.map((r, i) => (
                      <li
                        key={i}
                        className="bg-green-50 border border-green-200 rounded p-2 text-sm"
                      >
                        <div className="font-semibold text-gray-900">
                          {r.title}
                          {r.expected_lift_pp != null && (
                            <span className="ml-2 text-xs text-green-700">
                              +{r.expected_lift_pp}pp
                            </span>
                          )}
                        </div>
                        <div className="text-gray-700">{r.rationale}</div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold text-blue-700 uppercase mb-1">
                    Programs
                  </div>
                  <ul className="space-y-2">
                    {report.recommended_actions.programs.map((p, i) => (
                      <li
                        key={i}
                        className="bg-blue-50 border border-blue-200 rounded p-2 text-sm"
                      >
                        <div className="font-semibold text-gray-900">
                          {p.title}
                          {p.expected_lift_pp != null && (
                            <span className="ml-2 text-xs text-blue-700">
                              +{p.expected_lift_pp}pp
                            </span>
                          )}
                        </div>
                        {p.addresses && (
                          <div className="text-xs text-gray-500">
                            addresses: {p.addresses}
                          </div>
                        )}
                        <div className="text-gray-700">{p.rationale}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {report.recommended_actions.narrative && (
                <div className="bg-gray-50 border-l-4 border-indigo-400 p-3 mt-3 text-sm text-gray-800 whitespace-pre-line">
                  {report.recommended_actions.narrative}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupIntelligencePanel;
